import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Paper,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Security as SecurityIcon,
  SwapHoriz as TransferIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  Organization,
  OrganizationMember,
  OrganizationRole,
  getRoleLabel,
  getRoleColor,
  isRoleEqual,
  isRoleGreaterOrEqual,
} from '../../services/organizationApi';

interface OrganizationSettingsProps {
  organization: Organization;
  members: OrganizationMember[];
  onTransferOwnership: () => void;
  canTransfer: boolean;
}

const OrganizationSettings: React.FC<OrganizationSettingsProps> = ({
  organization,
  members,
  onTransferOwnership,
  canTransfer,
}) => {
  const owner = members.find(member => isRoleEqual(member.role, OrganizationRole.OWNER));
  const admins = members.filter(member => isRoleEqual(member.role, OrganizationRole.ADMIN));
  const regularMembers = members.filter(member => isRoleGreaterOrEqual(member.role, OrganizationRole.MEMBER));

  const getCurrentUserId = (): string => {
    return localStorage.getItem('gamora_user_id') || '';
  };

  const isCurrentUserOwner = (): boolean => {
    return owner?.user_id === getCurrentUserId();
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Organization Settings
      </Typography>

      {/* Permission Notice */}
      {!canTransfer && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Permission Required:</strong> Only the organization owner can access 
          organization settings and transfer ownership.
        </Alert>
      )}

      {/* Critical Settings */}
      {canTransfer && (
        <Paper sx={{ p: 3, mb: 3, border: '2px solid', borderColor: 'warning.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WarningIcon color="warning" sx={{ mr: 1 }} />
            <Typography variant="h6" color="warning.main">
              Critical Settings
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These settings can significantly impact your organization. Use with caution.
          </Typography>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 2 
          }}>
            <Box>
              <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TransferIcon sx={{ mr: 1 }} />
                    Transfer Ownership
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Transfer ownership of this organization to another member. 
                    This action cannot be undone.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={onTransferOwnership}
                    disabled={!canTransfer}
                  >
                    Transfer Ownership
                  </Button>
                </CardContent>
              </Card>
            </Box>
            
            <Box>
              <Card sx={{ border: '1px solid', borderColor: 'error.main' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Permanently delete this organization and all associated data. 
                    This action cannot be undone.
                  </Typography>
                  <Typography variant="caption" color="error.main">
                    Note: Delete option is available in the Overview tab
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Current Ownership Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SecurityIcon sx={{ mr: 1 }} />
          Current Ownership
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {owner && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Chip
              label={getRoleLabel(owner.role)}
              sx={{
                backgroundColor: getRoleColor(owner.role),
                color: 'white',
                fontWeight: 'bold',
              }}
              size="medium"
            />
            <Box>
              <Typography variant="subtitle1">
                {owner.user.username}
                {isCurrentUserOwner() && (
                  <Chip
                    label="You"
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {owner.user.email}
                {owner.user.company && ` • ${owner.user.company}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Owner since {new Date(owner.joined_at).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Ownership Responsibilities:</strong> As the organization owner, you have full control 
            over the organization, including the ability to add/remove members, update organization details, 
            and delete the organization. Consider carefully before transferring ownership.
          </Typography>
        </Alert>
      </Paper>

      {/* Role Management */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Role Management
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3 
        }}>
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Available Roles
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <Chip
                    label="Owner"
                    sx={{
                      backgroundColor: getRoleColor(OrganizationRole.OWNER),
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Owner (1 per organization)"
                  secondary="Full control over organization and members"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Chip
                    label="Admin"
                    sx={{
                      backgroundColor: getRoleColor(OrganizationRole.ADMIN),
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Admin (Multiple allowed)"
                  secondary="Administrative access and content management"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Chip
                    label="Member"
                    sx={{
                      backgroundColor: getRoleColor(OrganizationRole.MEMBER),
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Member (Multiple allowed)"
                  secondary="Standard access and content contribution"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Chip
                    label="Viewer"
                    sx={{
                      backgroundColor: getRoleColor(OrganizationRole.VIEWER),
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Viewer (Multiple allowed)"
                  secondary="Read-only access to organization content"
                />
              </ListItem>
            </List>
          </Box>
          
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Current Role Distribution
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">Owner</Typography>
                <Chip label="1" size="small" color="error" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">Admin</Typography>
                <Chip label={admins.length.toString()} size="small" color="primary" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">Member</Typography>
                <Chip label={regularMembers.length.toString()} size="small" color="success" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">Total</Typography>
                <Chip label={members.length.toString()} size="small" color="default" />
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Organization Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ mr: 1 }} />
          Organization Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3 
        }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Organization ID
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: 'grey.100', p: 1, borderRadius: 1 }}>
              {organization.id}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Created Date
            </Typography>
            <Typography variant="body2">
              {new Date(organization.created_at).toLocaleDateString()} at {new Date(organization.created_at).toLocaleTimeString()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Last Updated
            </Typography>
            <Typography variant="body2">
              {new Date(organization.updated_at).toLocaleDateString()} at {new Date(organization.updated_at).toLocaleTimeString()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Domain
            </Typography>
            <Typography variant="body2">
              {organization.domain}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Security Recommendations */}
      {canTransfer && (
        <Paper sx={{ p: 3, backgroundColor: 'info.light' }}>
          <Typography variant="h6" gutterBottom color="info.contrastText">
            Security Recommendations
          </Typography>
          <Typography variant="body2" color="info.contrastText" sx={{ mb: 2 }}>
            To maintain the security of your organization:
          </Typography>
          <List dense sx={{ color: 'info.contrastText' }}>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• Regularly review member roles and permissions"
                secondary="Ensure members have appropriate access levels"
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• Monitor organization activity"
                secondary="Keep track of member additions and role changes"
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• Use strong authentication"
                secondary="Encourage members to use strong passwords and 2FA if available"
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• Plan ownership transitions carefully"
                secondary="Ensure the new owner is trustworthy and capable"
              />
            </ListItem>
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default OrganizationSettings;
